const { fields } = require('./programmaticTagBaseFields');
const { MaxInsertsPerPage, DefaultConfig } = require('../dynamicInsert/constants');
const BoosterTagFields = require('./../booster/boosterTagFields');

const uiGroup = 'Built in settings applied on first call to relevantDigital.loadPrebid(...)';
const optimizationUiGroup = 'Auto-optimization settings';

const DEFAULT_BADGE = `
<div style="position: fixed; bottom: -2px;left: -2px; z-index: 1000000; border-color: white;border-style: solid;border-radius: 4px;border-width: 1px;background: #02a729;padding: 2px 7px;font-family: sans-serif;font-size: small;">
    <a href="{{link}}" target="{{target}}" style="text-decoration: none;">
        <span style="color: white;">
            {{infoText}}
        </span>
    </a>
</div>
`.trim();

const LOADPREBID_BOOLS = {
	runHbLessNow: [
		'Do ad-request immediately for placements without HB',
		{ props: { help: 'This implies "noAdsInitRequestAll" for GAM and affects order of callbacks, if using such' } },
	],
	hasSharedAdUnits: ['Has shared ad units'],
	allowCodeReuse: ['Reuse ad unit codes in between auctions'],
	logAsPlacementId: [
		'Use Yield placement ID when logging HBA data',
		{
			props: {
				help: `
This setting might be needed to filter placements in HBA reports when using custom ad unit codes.  
Drawback is that placements won't be mapped properly for historical data if re-creating placements inside Yield.`,
			},
		},
	],
	delayedAdserverLoading: ['[Google Ad Manager] Use delayed adserver loading'],
	noGpt: ['[Google Ad Manager] Don\'t let script load gpt.js'],
	collapseEmptyDivs: ['[Google Ad Manager] Collapse empty divs'],
	collapseBeforeAdFetch: ['[Google Ad Manager] Collapse slots before ad-requests'],
	noAdsInitRequestAll: ['[Google Ad Manager] Don\'t request all slot on first ad-request'],
	alwDivImplyNoReqAll: [
		'[Google Ad Manager] \'allowedDivIds\' set implies \'noAdsInitRequestAll\'',
		{
			props: {
				help: `
When filtering placements using "allowedDivIds" and using this flag - it means "noAdsInitRequestAll" 
will be used even if not explicitly set. This is to make sure no other ad slots are loaded except of the selected ones. 
(Recommended)`,
			},
		},
	],
	loadUnknownSlots: ['[Google Ad Manager / Xandr] Load unknown slots'],
};

module.exports = [
	{
		type: 'JavaScript',
		description: 'Custom Config JavaScript code',
		name: 'rlvCfgJs',
		uiGroup,
	},
	{
		type: 'Object',
		name: 'rlvTimeouts',
		description: 'Timeout fields to override from programmatic tag',
		fields: fields.filter((fld) => fld.isTimeoutField).map((fld) => ({
			...fld,
			defaultValue: null,
		})),
		uiGroup,
	},
	{
		type: 'Boolean',
		description: 'Enable bid caching',
		name: 'rlvBidCache',
		uiGroup,
	},
	{
		type: 'Boolean',
		description: 'Enable Shadow DOM',
		name: 'rlvEnableShadowDom',
		uiGroup,
	},
	{
		type: 'Boolean',
		description: 'Inject functionality into Google IMA SDK to track instream video impressions in HBA',
		name: 'rlvImaInject',
		props: {
			help: 'Only recommended during testing of instream video as this might potentially cause issues upon future updates of the IMA SDK. Please consult your video player\'s documentation and our help section for guidelines on how to track video impressions.',
		},
		uiGroup,
	},
	...BoosterTagFields.PbConfigTagFields,
	{
		type: 'Object',
		description: 'Ad transparency',
		name: 'rlvTransparency',
		fields: [
			{
				type: 'Boolean',
				name: 'badge',
				description: 'Show ad information badge',
			},
			{
				type: 'Object',
				description: 'UI settings',
				name: 'uiSettings',
				fields: [
					{
						type: 'HTML',
						name: 'badgeHtml',
						description: 'HTML for ad information badge',
						defaultValue: DEFAULT_BADGE,
					},
					{
						type: 'String',
						name: 'infoText',
						description: 'Text in badge',
						isRequired: true,
						defaultValue: 'Ad info',
					},
					{
						type: 'String',
						name: 'target',
						description: 'Info window open behavior (target)',
						defaultValue: '_blank',
						isRequired: true,
						hasOptions: true,
						options: [
							['_blank', 'Open in new tab (_blank)'],
							['_self', 'Open inside ad (_self)'],
							['_top', 'Open in current tab (_top)'],
							['_parent', 'Open in parent frame (_parent)'],
						].map(([name, label]) => ({ name, label })),
					},
					{
						type: 'String',
						name: 'logoUrl',
						description: 'Override logo URL on info page (replaces Relevant logo if set)',
					},
					{
						type: 'String',
						name: 'link',
						description: 'Ad information page base URL',
						isRequired: true,
						defaultValue: 'https://cdn.relevant-digital.com/transparency/adInfo.html?settings=',
						// defaultValue: 'https://dev-api.relevant-digital.com/html/adInfo.html?settings=',
					},
				],
			},
			{
				type: 'Boolean',
				name: 'dsa',
				description: 'Enable sending DSA data',
			},
			{
				type: 'String',
				name: 'dsarequired',
				description: 'DSA required',
				defaultValue: '1',
				hasOptions: true,
				isRequired: true,
				options: [
					['0', 'Not required'],
					['1', 'Supported, bid responses with or without DSA object will be accepted'],
					['2', 'Required, bid responses without DSA object will not be accepted'],
					['3', 'Required, bid responses without DSA object will not be accepted, Publisher is an Online Platform'],
				].map(([name, label]) => ({ name, label })),
			},
			{
				type: 'Boolean',
				name: 'enforceDsa',
				description: 'Enforce DSA required by rejecting bids',
			},
			{
				type: 'String',
				name: 'datatopub',
				description: 'Data to publisher',
				defaultValue: '2',
				hasOptions: true,
				isRequired: true,
				options: [
					['0', 'Do not send transparency data'],
					['1', 'Optional to send transparency data'],
					['2', 'Send transparency data'],
				].map(([name, label]) => ({ name, label })),
			},
			{
				type: 'Boolean',
				name: 'letAdRender',
				description: 'Don\'t show own information badge if bid indicates "adrender=1"',
			},
			{
				type: 'Array',
				description: 'DSA Transparency objects to send',
				name: 'transparency',
				fields: [
					{
						type: 'String',
						name: 'domain',
						description: 'Domain',
						isRequired: true,
					},
					...[[1, 'Profiling'], [2, 'Basic advertising'], [3, 'Precise geolocation']].map(([nr, desc]) => ({
						type: 'Boolean',
						name: `param${nr}`,
						description: desc,
					})),
				],
			},
		],
	},
	{
		type: 'Object',
		description: 'Dynamic Placement Insertion (may inflate Auction metric in reporting)',
		name: 'rlvDynamicInsertion',
		fields: [
			{
				type: 'Boolean',
				name: 'enableBackgroundScan',
				description: 'Enable continious background scan',
				defaultValue: DefaultConfig.enableBackgroundScan,
			},
			{
				type: 'Number',
				name: 'maxPerPageView',
				description: 'Maximum number of dynamic inserts per page view',
				defaultValue: MaxInsertsPerPage.default,
				props: { between: { low: MaxInsertsPerPage.min, high: MaxInsertsPerPage.max } },
			},
			{
				type: 'Number',
				name: 'scanInterval',
				description: 'Scan interval in ms for DOM scanning',
				defaultValue: DefaultConfig.scanInterval,
			},
			{
				type: 'Number',
				name: 'pixels',
				description: 'Look-ahead distance in pixels for DOM scanning',
				defaultValue: DefaultConfig.pixels,
			},
		],
	},
	{
		type: 'Object',
		description: 'GAM Lazy Load Settings',
		name: 'rlvGamLazyLoad',
		uiHelp: { query: 'GAM Lazy Load Settings', question: 'Explain the lazy-loading feature for ad placements, including the GAM Lazy Load Settings (rlvGamLazyLoad) and how it affects ad performance' },
		fields: [
			{
				type: 'Boolean',
				name: 'enabled',
				defaultValue: false,
				description: 'Enable lazy loading',
			},
			{
				type: 'Number',
				name: 'fetchMarginPercent',
				defaultValue: null,
				description: 'Fetch margin in percent',
			},
			{
				type: 'Number',
				name: 'renderMarginPercent',
				defaultValue: null,
				description: 'Render margin in percent',
			},
			{
				type: 'Number',
				name: 'mobileScaling',
				description: 'Mobile multiplier',
				defaultValue: null,
			},
		],
	},
	{
		type: 'Object',
		description: 'Price floor module enforcements',
		name: 'rlvFloorEnforce',
		omitWhenDefault: true,
		fields: [
			{
				type: 'Boolean',
				name: 'enforceJS',
				defaultValue: true,
				description: 'Let Prebid.JS enforce price floors by reject bids below it',
				omitWhenDefault: true,
			},
			{
				type: 'Boolean',
				name: 'floorDeals',
				description: 'Enforce price floors also for deals',
				omitWhenDefault: true,
			},
		],
	},
	{
		type: 'Object',
		description: 'Default settings for relevantDigital.loadPrebid()',
		name: 'rlvLoadPrebidDefaults',
		omitWhenDefault: true,
		fields: Object.entries(LOADPREBID_BOOLS).map(([name, [desc, extra]]) => ({
			type: 'Boolean',
			name,
			description: `${desc} (${name})`,
			omitWhenDefault: true,
			...extra,
		})),
	},
	{
		type: 'Number',
		description: '% of users where selected auto-optimization settings should be applied',
		name: 'rlvOptPerc',
		defaultValue: 90,
		props: { between: { low: 0, high: 100 } },
		uiGroup: optimizationUiGroup,
		optiField: true,
		isRequired: true,
	},
	{
		type: 'Number',
		description: '% of auto-optimized users used to find new optimal values',
		name: 'rlvBenchPerc',
		defaultValue: 10,
		props: { between: { low: 0, high: 100 } },
		uiGroup: optimizationUiGroup,
		optiField: true,
		isRequired: true,
	},
	{
		type: 'Number',
		description: 'Delay auctions at most this number of milliseconds when awaiting optimization data',
		name: 'rlvOptWait',
		uiGroup: optimizationUiGroup,
		optiField: true,
		isRequired: true,
	},
	{
		type: 'Number',
		description: 'Never delay auctions when optimization data is fresher than this number of minutes',
		name: 'rlvOptTimeoutMinutes',
		defaultValue: 1440,
		uiGroup: optimizationUiGroup,
		optiField: true,
		isRequired: true,
	},
	{
		type: 'Boolean',
		description: 'Add "Optimization freshness" + "Optimization group" HBA dimensions',
		name: 'rlvOptDebugDims',
		uiGroup: optimizationUiGroup,
		optiField: true,
		omitWhenDefault: true,
	},
];
